<template>
  <div class="relative w-full flex-1 flex flex-col items-center justify-between">
    <ClientOnly>
      <button
        class="absolute right-4 sm:right-24 top-4 w-[18px] h-[18px] u-center rounded-full bg-[#B2C0FF] text-[#040C47] text-md font-bold"
        @click="showHelpModal()"
      >
        i
      </button>
      <PreviousGamesCard v-if="!isMobile && screenWidth" class="absolute right-0 top-1/4" game-name="emojile" :start-date="startDate" />
      <OButton
        v-else
        pure
        class="absolute left-4 top-5 w-9 h-9 u-center rounded leaderboardButtonBackground"
        :href="`${isWebview ? '/mobile' : ''}/oyun/leaderboard/emojile`"
      >
        <OIcon icon="mobile-leaderboard-icon" svg-class="text-[#000746]" />
      </OButton>
      <LeaderboardCard v-if="!isMobile && screenWidth" ref="leaderboard" class="absolute left-0 top-1/4" game-name="emojile" />
      <OButton
        v-else
        pure
        class="absolute left-4 top-16 w-9 h-9 u-center rounded bg-[#B2C0FF]"
        :href="`${isWebview ? '/mobile' : ''}/oyun/skor/emojile?month=${date.month()}&year=${date.year()}`"
      >
        <OIcon icon="recent" svg-class="text-[#000746]" />
      </OButton>
    </ClientOnly>

    <!--logo-->
    <div class="u-center mt-4">
      <OImage loading="lazy" src="/images/emojile.png" class="emojile-logo w-[162px] h-[55px] sm:w-[188px] sm:h-[65px]" />
      <p class="u-center ml-2 sm:ml-[10px] text-[#FBDA2E] text-[17px] sm:text-22 font-bold leading-24 sm:leading-30">#{{ paddedNumber }}</p>
    </div>

    <div class="flex flex-col items-center sm:max-w-[1100px]">
      <!--emoji-->
      <div class="flex items-center mb-3 text-24 text-[#B2C0FF]">
        <div v-for="(item, index) in question" :key="item.title" class="flex items-center">
          <div :title="item.title" class="box box-empty h-[55px] w-[55px] u-center text-32 rounded-md">
            {{ item.emoji }}
          </div>
          <span v-if="index < question.length - 1" class="mx-2">&nbsp;+&nbsp;</span>
        </div>
      </div>
      <!--hint-->
      <div v-if="false" class="text-white text-sm text-center mb-2">
        <span v-if="hintText">ipucu: {{ hintText }}</span>
      </div>
      <!--cards-->
      <div v-for="(row, xIndex) in range(0, 6)" :key="row" class="flex space-x-1">
        <div v-for="(item, yIndex) in range(0, answer.length)" :key="item">
          <div
            v-if="row === rowIndex && columnIndex"
            class="box box-empty sm:w-[43px] sm:h-[43px] w-9 h-9 u-center mt-1 text-white text-xl font-semibold rounded-md"
            :class="{ 'scale-0 ease-out duration-500': spinning }"
          >
            {{ prediction[yIndex] }}
          </div>
          <div
            v-else
            class="box sm:w-[43px] sm:h-[43px] w-9 h-9 u-center mt-1 text-white text-xl rounded-md"
            :class="background(xIndex, yIndex)"
          >
            {{ boardState[xIndex] && boardState[xIndex][yIndex] }}
          </div>
        </div>
      </div>
    </div>
    <!--keyboard-->
    <div
      class="keyboard w-full sm:max-w-[768px] p-3 flex flex-col space-y-2 rounded-t-2xl bg-white-10"
      :class="{
        'pb-6': isWebview,
      }"
    >
      <div v-for="key in keyboard" :key="key[0]" class="flex items-center justify-between w-full space-x-1">
        <button
          v-for="letter in key"
          :key="letter"
          class="keyboard-button flex-1 h-[42px] sm:h-12 px-1 u-center text-sm sm:text-md font-medium rounded-md"
          :class="keyboardStyle(letter)"
          @click.prevent="handlePressKey(letter)"
        >
          <IconDelete v-if="letter === 'delete'" class="w-8" />
          <span v-else>{{ letter }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, onMounted, ref, ssrRef, useAsync, useContext, useRoute } from '@nuxtjs/composition-api';
import { clone, range, throttle } from 'lodash';
import { useWindowSize } from '@vueuse/core';
import { OButton, OImage, OIcon } from '~/modules/common/components/ui';
import { useModal, useToast } from '~/composables/useModal';
import IconDelete from '~/static/svg/delete.svg?inline';
import { localStore } from '~/modules/common/utils/localStorageUtils';
import { useEmojileMeta } from '~/modules/emojile/utils/emojileMeta';
import { useTracking } from '~/modules/tracking/composables/useTracking';
import { useAuth } from '~/modules/auth/state/useAuth';
import { useDevice } from '~/composables/useDevice';
import PreviousGamesCard from '~/modules/oyun/components/previousGames/PreviousGamesCard.vue';
import LeaderboardCard from '~/modules/oyun/components/leaderboard/LeaderboardCard.vue';
import { dayjs } from '~/modules/common/utils/dateUtils';

export default defineComponent({
  head() {},
  props: {
    isWebview: {
      type: Boolean,
      required: false,
    },
  },
  components: { IconDelete, PreviousGamesCard, LeaderboardCard, OButton, OImage, OIcon },
  setup(props) {
    const route = useRoute();
    const { user, isLoggedIn } = useAuth();
    const { openModal } = useModal();
    const { toastSuccess, toastDanger } = useToast();
    const { $cache, $service } = useContext();
    const { isMobile } = useDevice();
    const id = ssrRef(null); // game id
    const gameNumber = ssrRef(0); // game number
    const nextQuestionTS = ssrRef(0);
    const question = ssrRef([]);
    const answer = ssrRef(''); // answer
    const relatedItems = ssrRef([]); // editor articles
    const hint = ssrRef([]);
    const prediction = ssrRef([]); // user prediction
    const boardState = ssrRef([]); // prediction history
    const spinning = ref(false);
    const rowIndex = ssrRef(0); // active typo row
    const columnIndex = ref(0); // active typo column
    const evaluations = ssrRef([]); // all letter states
    const gameStatus = ssrRef('');
    const score = ssrRef(0);
    const isStarted = ref(false);
    const startDate = ssrRef(0);
    const date = dayjs(startDate.value);
    const leaderboard = ref(null);

    const keyboardStatus = ssrRef({
      correct: [],
      present: [],
      absent: [],
    });
    const keyboard = [
      ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P', 'Ğ', 'Ü'],
      ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ş', 'İ'],
      ['ENTER', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', 'Ö', 'Ç', 'delete'],
    ];
    const { width } = useWindowSize();
    const screenWidth = computed(() => width.value > 1280);

    $cache('public, max-age=30');

    // hint
    const hintText = computed(() => {
      if (hint.value.length === 0) return null;
      if (rowIndex.value > 4 && hint.value.length > 2) {
        return `${hint.value[0]}, ${hint.value[1]}, ${hint.value[2]}`;
      } else if (rowIndex.value > 3 && hint.value.length > 1) {
        return `${hint.value[0]}, ${hint.value[1]}`;
      } else if (rowIndex.value > 2 && hint.value.length > 0) {
        return hint.value[0];
      } else {
        return '';
      }
    });
    // box background style
    const background = (xIndex, yIndex) => {
      if (evaluations.value[xIndex] && evaluations.value[xIndex][yIndex]) {
        const status = evaluations.value[xIndex][yIndex];
        return status === 'correct' ? 'bg-[#11B076]' : status === 'present' ? 'bg-[#F3940E]' : 'bg-[#0E1947]';
      }
      return 'box-empty';
    };
    // keyboard button style
    const keyboardStyle = (letter) => {
      if (keyboardStatus.value.correct.includes(letter)) return 'bg-[#11B076] text-white';
      if (keyboardStatus.value.present.includes(letter)) return 'bg-[#F3940E] text-white';
      if (keyboardStatus.value.absent.includes(letter)) return 'bg-[#0E1947] text-white';
      return 'bg-[#EEF2F6] sm:hover:bg-gray-200 text-[#61656A]';
    };
    // calculate user score
    const handleCalculateScore = () => {
      const isWin = isAllCorrect(evaluations.value[evaluations.value.length - 1]);
      const basePoints = { 1: 20, 2: 15, 3: 10, 4: 5, 5: 3, 6: 2 };
      if (isWin) {
        score.value = basePoints[evaluations.value.length] * answer.value.length;
      } else {
        const successScenarioArray = Array.from({ length: answer.value }, () => false);
        evaluations.value.map((step) => {
          step.filter((item, index) => {
            if (item === 'correct') {
              successScenarioArray[index] = 'correct';
            }
          });
        });
        const successCount = successScenarioArray.filter((item) => item === 'correct').length;
        score.value = successCount * 1;
      }
    };

    // is all correct?
    const isAllCorrect = (states) => {
      return states?.every((state) => state === 'correct');
    };
    // show modal
    const handleResultModal = throttle(async () => {
      if (isAllCorrect(evaluations.value[evaluations.value.length - 1])) {
        toastSuccess('Tebrikler', 900);
        gameStatus.value = 'WIN';
        window.dataLayer.push({
          event: 'emojile_finished',
          step: rowIndex.value.toString(),
        });
      } else if (evaluations.value.length === 6) {
        toastSuccess(answer.value, 900);
        gameStatus.value = 'LOST';
      } else {
        return;
      }
      if (!isMobile) await leaderboard.value.getLeaderboard();
      setTimeout(() => {
        openModal(import('~/modules/emojile/components/ModalEmojile' /* webpackChunkName: "ModalEmojile" */), {
          evaluations: evaluations.value,
          relatedItems: relatedItems.value,
          gameNumber: gameNumber.value,
          answer: answer.value,
          score: score.value,
          nextQuestionTS: nextQuestionTS.value,
          isToday: !route.value.query.game,
          isWebview: props.isWebview,
        });
      }, 1000);
    }, 1000);
    // open how to play modal
    const showHelpModal = () => {
      openModal(import('~/modules/emojile/components/ModalEmojileHelp' /* webpackChunkName: "ModalEmojileHelp" */));
      // setTimeout(() => {
      //   const elem = document.getElementById('modal');
      //   if (elem) {
      //     elem.style.transform = 'translateY(0)';
      //   }
      // }, 150);
    };
    // show how to play modal
    const howToPlay = () => {
      const data = localStore.getItem('o-emjl-htp');
      if (data) return;
      localStore.setItem('o-emjl-htp', true);
      showHelpModal();
    };
    // submit
    const handleSubmitWord = () => {
      const states = [];
      const _answer = clone(answer.value.split(''));
      // find corrects
      prediction.value.forEach((letter, index) => {
        if (letter === answer.value[index]) {
          states[index] = 'correct';
          _answer[index] = null;
          keyboardStatus.value.correct.push(letter);
        }
      });
      // find others
      prediction.value.forEach((letter, index) => {
        if (states[index]) return;
        if (_answer.includes(letter)) {
          states[index] = 'present';
          _answer[_answer.indexOf(letter)] = null;
          keyboardStatus.value.present.push(letter);
        } else {
          states[index] = 'absent';
          keyboardStatus.value.absent.push(letter);
        }
      });
      evaluations.value.push([...states]);
    };

    // check word with onedio search api
    const isWordValid = async () => {
      if (answer.value === prediction.value.join('')) return true;
      const result = await $service.search.searchArticle({ query: prediction.value.join(''), page: 1, limit: 3, withFacets: false });
      return result?.data.length > 0;
    };

    const handlePressKey = async (key) => {
      if (gameStatus.value === 'WIN' || gameStatus.value === 'LOST' || spinning.value) return;
      if (key.toLowerCase() === 'delete') {
        // delete pressed
        if (columnIndex.value === 0) return;
        prediction.value.pop();
        columnIndex.value = columnIndex.value - 1;
      } else if (key.toLowerCase() === 'enter') {
        // enter pressed
        if (columnIndex.value !== answer.value.length) {
          toastDanger('Kelime çok kısa');
          return;
        }
        spinning.value = true;
        // word check
        if (!(await isWordValid())) {
          toastDanger('Kelime listesinde bulunamadı!');
          setTimeout(() => {
            spinning.value = false;
          }, 1000);
          window.dataLayer.push({
            event: 'emojile_tried',
            event_type: 'fail',
            step: (rowIndex.value + 1).toString(),
            count: 1,
          });
          return;
        }
        window.dataLayer.push({
          event: 'emojile_tried',
          event_type: 'success',
          step: (rowIndex.value + 1).toString(),
          count: 1,
        });
        handleSubmitWord();
        handleCalculateScore();
        setTimeout(async () => {
          boardState.value.push([...prediction.value]);
          spinning.value = false;
          rowIndex.value++;
          columnIndex.value = 0;
          prediction.value = [];
          await setGameData();
          setTimeout(() => {
            handleResultModal();
          }, 500);
        }, 500);
      } else {
        // letter pressed
        if (columnIndex.value === answer.value.length) return;
        if (!isStarted.value && columnIndex.value === 0 && rowIndex.value === 0) {
          isStarted.value = true;
          window.dataLayer.push({
            event: 'emojile_started',
          });
        }
        prediction.value[columnIndex.value] = key;
        columnIndex.value = columnIndex.value + 1;
      }
    };

    const keyboardListener = () => {
      document.addEventListener('keyup', (e) => {
        const pressedKey = String(e.key);
        if (pressedKey === 'Backspace') {
          handlePressKey('delete');
          return;
        }
        if (pressedKey === 'Enter') {
          handlePressKey('enter');
          return;
        }
        const found = pressedKey.match(/[a-zA-ZçığöüşÇĞIİĞÖÜŞ]/gi);
        if (!found || found.length > 1) {
        } else {
          handlePressKey(found[0].toLocaleUpperCase('tr'));
        }
      });
    };

    onMounted(() => {
      keyboardListener();
      getGameData();
      howToPlay();
    });

    const setGameData = async () => {
      const isEnded = isAllCorrect(evaluations.value?.[evaluations.value.length - 1]) || evaluations.value?.length === 6;
      const data = {
        id: id.value,
        gameNumber: gameNumber.value,
        prediction: prediction.value,
        boardState: boardState.value,
        rowIndex: rowIndex.value,
        gameStatus: isEnded ? 'done' : 'in progress',
        score: score.value,
        keyboardStatus: keyboardStatus.value,
        evaluations: evaluations.value,
      };
      if (isLoggedIn.value) {
        const params = {
          gameType: 'emojile',
          gameId: id.value,
          gameStatus: isEnded ? 'done' : 'in progress',
          score: score.value,
          statusData: data,
        };
        await $service.game.addPlayerStatus(params);
      } else {
        const LSGameData = localStore.getItem('o-emojile') || {};
        LSGameData[id.value] = data;
        localStore.setItem('o-emojile', LSGameData);
      }
    };

    const getGameData = () => {
      if (!isLoggedIn.value) {
        const data = localStore.getItem('o-emojile')?.[id.value];
        if (!data) return;
        id.value = data.id;
        prediction.value = data.prediction;
        boardState.value = data.boardState;
        rowIndex.value = data.rowIndex;
        gameStatus.value = data.gameStatus;
        keyboardStatus.value = data.keyboardStatus;
        evaluations.value = data.evaluations;
        handleCalculateScore();
      }
      handleResultModal();
    };

    const getUserGameData = (data) => {
      if (!isLoggedIn.value) return;
      if (!data) return;
      id.value = data.id;
      prediction.value = data.prediction;
      boardState.value = data.boardState;
      rowIndex.value = data.rowIndex;
      gameStatus.value = data.gameStatus;
      keyboardStatus.value = data.keyboardStatus;
      evaluations.value = data.evaluations;
      handleCalculateScore();
    };
    const paddedNumber = computed(() => {
      return String(gameNumber.value).padStart(4, '0');
    });

    useAsync(async () => {
      if (process.server) {
        try {
          let result = null;
          const userParams = isLoggedIn.value ? { withStatuses: true, playerId: user.value._id } : {};
          if (route.value.query.game) {
            result = (await $service.game.getGameById({ gameType: 'emojile', gameId: route.value.query.game, ...userParams })).data;
          } else {
            result = (await $service.game.getDailyQuestion({ gameType: 'emojile', ...userParams })).data;
          }
          id.value = result?.id;
          question.value = result?.question;
          answer.value = result?.answer;
          hint.value = result?.hint;
          gameNumber.value = result?.gameNumber;
          relatedItems.value = result?.relatedItems;
          nextQuestionTS.value = result?.questionExpirationTimestamp;
          startDate.value = result?.startDate;
          getUserGameData(result.playerStatus?.statusData);
        } catch (error) {
          console.log('emojile err: ', error);
        }
      }
    });

    useEmojileMeta();

    // ------- Google Pageview Event Beginning -------
    const { sendPageviewEvent } = useTracking();
    sendPageviewEvent({
      page: {
        name: `Emojile | Yeni Nesil Wordle!`,
        type: 'Onedio Oyun',
      },
    });
    // ------- Google Pageview Event End -------

    return {
      question,
      answer,
      hintText,
      boardState,
      rowIndex,
      columnIndex,
      spinning,
      keyboard,
      prediction,
      range,
      handlePressKey,
      background,
      keyboardStyle,
      showHelpModal,
      isMobile,
      paddedNumber,
      startDate,
      date,
      leaderboard,
      screenWidth,
    };
  },
});
</script>

<style lang="scss">
.box {
  border: 1px solid rgba(255, 255, 255, 0.35);
  box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.05);
}
.box-empty {
  background: rgba(255, 255, 255, 0.3);
}

@media (max-width: 374px) {
  .box {
    width: 28px;
    height: 28px;
    font-size: 20px;
  }
  .keyboard {
    padding: 4px;
    &-button {
      height: 36px;
    }
  }
  .emojile-logo {
    margin: 5px 0;
    width: 100px;
    height: 34px;
  }
}
.leaderboardButtonBackground {
  background: linear-gradient(39deg, #ffba08 5.42%, #fec010 37.01%, #fae63c 103.46%);
}
</style>
